import React, { useRef, useEffect } from 'react';
import colors from '../../theme/colors';

const NODE_COUNT = 15;
const NODE_RADIUS_RATIO = 0.005; // Node radius as a ratio of min(width, height)
const LINE_DISTANCE_RATIO = 0.1; // Line distance as a ratio of min(width, height)
const ACCENT_COLOR = colors.accent;

function randomBetween(a: number, b: number) {
  return a + Math.random() * (b - a);
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const HeroCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodes = useRef<Node[]>([]);
  const sizeRef = useRef({ width: 0, height: 0 });

  // Helper to get current canvas size
  const getCanvasSize = () => {
    const parent = canvasRef.current?.parentElement;
    if (parent) {
      const rect = parent.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    }
    return { width: window.innerWidth, height: 400 };
  };

  // Initialize nodes based on current size
  const initNodes = (width: number, height: number) => {
    nodes.current = Array.from({ length: NODE_COUNT }, () => ({
      x: randomBetween(0, width),
      y: randomBetween(0, height),
      vx: randomBetween(-0.3, 0.3),
      vy: randomBetween(-0.3, 0.3),
    }));
  };

  useEffect(() => {
    const resizeCanvas = () => {
      const { width, height } = getCanvasSize();
      sizeRef.current = { width, height };
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = width;
        canvas.height = height;
      }
      initNodes(width, height);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { width, height } = sizeRef.current;
      ctx.clearRect(0, 0, width, height);
      const minDim = Math.min(width, height);
      const NODE_RADIUS = NODE_RADIUS_RATIO * minDim;
      const LINE_DISTANCE = LINE_DISTANCE_RATIO * minDim;

      // Draw lines
      for (let i = 0; i < nodes.current.length; i++) {
        for (let j = i + 1; j < nodes.current.length; j++) {
          const a = nodes.current[i];
          const b = nodes.current[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINE_DISTANCE) {
            ctx.save();
            ctx.globalAlpha = 0.5 * (1 - dist / LINE_DISTANCE);
            ctx.strokeStyle = ACCENT_COLOR;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      // Draw nodes
      for (const node of nodes.current) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(node.x, node.y, NODE_RADIUS, 0, 2 * Math.PI);
        ctx.fillStyle = ACCENT_COLOR;
        ctx.globalAlpha = 0.85;
        ctx.shadowColor = ACCENT_COLOR;
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.restore();
      }
    };

    const update = () => {
      const { width, height } = sizeRef.current;
      for (const node of nodes.current) {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;
      }
    };

    const animate = () => {
      update();
      draw();
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
};

export default HeroCanvas; 